import type { TaskWithFlags } from "@desk/contracts";
import type { BoardCardProps, BoardColumnModel, BoardDnd, BoardProps } from "../types";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { DeadlineLabel, SnoozeBadge, SyncBadge } from "@/components/task-bits";
import { cn } from "@/lib/utils";

function Card({ task, card, dnd }: { task: TaskWithFlags; card: BoardCardProps; dnd: BoardDnd }) {
  const d = dnd.useCard(task.id);
  const closed = task.status === "done" || task.status === "cancelled";
  return (
    <div
      ref={d.setNodeRef}
      style={d.style}
      {...d.attributes}
      {...d.listeners}
      data-task-id={task.id}
      onClick={card.onOpen}
      className={cn(
        "group/card relative cursor-grab rounded-lg border border-border bg-card px-3 py-2.5 text-left shadow-[0_1px_1px_rgba(37,36,34,0.03)] transition-[box-shadow,border-color] active:cursor-grabbing",
        "hover:border-foreground/15 focus-visible:outline-2 focus-visible:outline-ring",
        card.selected && "border-foreground/30",
        d.isDragging && "cursor-grabbing border-foreground/20",
      )}
    >
      <div className="flex items-start gap-2">
        <p className={cn("clamp-2 min-w-0 flex-1 break-words text-[14px] leading-[20px]", closed && "text-muted-foreground line-through decoration-foreground/25")}>
          {task.title}
        </p>
        <div
          className="-mr-1 -mt-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/card:opacity-100"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {card.menu}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-muted-foreground">
        {task.priority !== "none" && <PriorityIcon priority={task.priority} className="size-3.5" />}
        {task.readableId !== "DSK-0" && <span className="tabular text-subtle">{task.readableId}</span>}
        {task.deadline && !closed && <DeadlineLabel deadline={task.deadline} today={card.today} />}
        <SnoozeBadge task={task} timezone={card.timezone} />
        <SyncBadge mark={card.sync} />
        {card.location && <span className="max-w-[160px] truncate text-subtle">{card.location}</span>}
        {task.checklist.length > 0 && (
          <span className="tabular text-subtle">
            {task.checklist.filter((c) => c.done).length}/{task.checklist.length}
          </span>
        )}
      </div>
    </div>
  );
}

function Column({ col, card, dnd }: { col: BoardColumnModel; card: BoardProps["card"]; dnd: BoardDnd }) {
  const drop = dnd.useColumn(col.status);
  return (
    <section
      ref={drop.setNodeRef}
      aria-label={`${col.label}, ${col.tasks.length} tasks`}
      className={cn(
        "flex w-[272px] shrink-0 flex-col rounded-xl p-1.5 transition-colors",
        drop.isOver ? "bg-selected" : "bg-foreground/[0.025] dark:bg-white/[0.025]",
      )}
    >
      <header className="flex h-8 items-center gap-2 px-1.5">
        <StatusIcon status={col.status} className="size-3.5" />
        <h2 className="font-sans text-[13px] font-semibold tracking-normal text-foreground/85">{col.label}</h2>
        <span className="text-[12px] text-subtle tabular">{col.tasks.length}</span>
      </header>
      <div className="flex min-h-16 flex-col gap-1.5">
        {col.tasks.map((t) => (
          <Card key={t.id} task={t} card={card(t)} dnd={dnd} />
        ))}
      </div>
    </section>
  );
}

export function PaperBoard({ columns, card, dnd, empty }: BoardProps) {
  const total = columns.reduce((n, c) => n + c.tasks.length, 0);
  return (
    <div className="-mx-2" data-wide>
      {total === 0 && empty}
      <div className="flex gap-3 overflow-x-auto px-2 pb-6">
        {columns.map((c) => (
          <Column key={c.status} col={c} card={card} dnd={dnd} />
        ))}
      </div>
    </div>
  );
}
